import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useCart, useAuth } from '../context/AppContext';
import { Qty, OrderSummary, Steps, Badge, Empty, Loading, Spinner, fmtDate, fmtMoney, fmtSize, US_STATES, CA_PROVINCES, COUNTRIES, CountrySelect, useToast } from '../components/UI';
import { getShippingRates, calculateStripeTax, createOrder, getOrders, getOrder, cancelOrder, linkPhoto, fulfillOrder, uploadPhoto, validateCoupon, validateAddress, getGlobalSettings, getTrainingVideos, trainingVideoUrl } from '../api';

// Phase 6 — lazily loaded, only when a direct_wholesale company's Finalize
// screen actually mounts. Loading Stripe.js unconditionally for every company
// would mean invoice_retail companies (who never touch Stripe) still need
// js.stripe.com/api.stripe.com/etc. reachable — a real problem for networks
// with restrictive domain whitelists (e.g. a cruise ship's onboard network).
let _stripePromise = null;
function getStripePromise() {
  if (_stripePromise) return _stripePromise;
  if (!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) return null;
  _stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
  return _stripePromise;
}

const STEPS = ['Products', 'Cart & Shipping', 'Finalize', 'Done'];

// ═══════════════════════════════════════════════════════════════
//  ITEM DETAIL
// ═══════════════════════════════════════════════════════════════
export function ItemDetailScreen() {
  const navigate      = useNavigate();
  const location      = useLocation();
  const { toast }     = useToast();
  const { addItem, cartCount } = useCart();
  const product       = location.state?.product;
  const [quantity, setQuantity] = useState(1);

  if (!product) return (
    <div className="page">
      <button className="btn btn-sm" onClick={() => navigate('/products')}>← Back</button>
      <p style={{ marginTop:20, color:'var(--text2)' }}>Product not found.</p>
    </div>
  );

  const lineTotal = product.retailPrice * quantity;

  const handleAdd = (goCart = false) => {
    // Always creates NEW line item — same product = new line item
    addItem({
      id:             Date.now().toString(),
      productId:      product.id,
      productName:    product.name,
      sku:            product.sku,
      retailPrice:    product.retailPrice,
      wholesalePrice: product.wholesalePrice || 0,
      imageUrl:       product.imageUrl || '',
      quantity,
    });
    toast(`${product.name} added to cart`, 'success');
    if (goCart) navigate('/cart');
    else navigate('/products');
  };

  return (
    <div className="page">
      <button className="btn btn-sm" style={{ marginBottom:20 }} onClick={() => navigate(-1)}>← Back</button>
      <div className="sidebar">
        <div className="card">
          <div style={{ width:'100%', borderRadius:'var(--radius)', overflow:'hidden', background:'var(--bg)', marginBottom:16 }}>
            {product.imageUrl
              ? <img src={product.imageUrl} alt={product.name} style={{ width:'100%', height:'auto', maxHeight:280, objectFit:'contain', display:'block' }} />
              : <div style={{ height:160, display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, color:'var(--text3)' }}>{product.name}</div>
            }
          </div>
          <div style={{ fontSize:20, fontWeight:700, marginBottom:4 }}>{product.name}</div>
          <div style={{ fontSize:12, color:'var(--text3)', marginBottom:8 }}>SKU: {product.sku}</div>
          {product.description && <div style={{ fontSize:13, color:'var(--text2)' }}>{product.description}</div>}
        </div>

        <div className="card" style={{ alignSelf:'start' }}>
          <div style={{ fontSize:28, fontWeight:700, color:'var(--brand)', marginBottom:4 }}>${product.retailPrice.toFixed(2)}</div>
          <div style={{ fontSize:12, color:'var(--text3)', marginBottom:20 }}>per item</div>
          <hr className="divider" />
          <div className="field">
            <label className="label">Quantity</label>
            <Qty value={quantity} onChange={setQuantity} />
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
            <span style={{ fontSize:13, color:'var(--text2)' }}>Line total</span>
            <span style={{ fontSize:18, fontWeight:700 }}>${lineTotal.toFixed(2)}</span>
          </div>
          <button className="btn btn-primary btn-full btn-lg" style={{ marginBottom:10 }} onClick={() => handleAdd(false)}>
            Add to cart
          </button>
          {cartCount > 0 && (
            <button className="btn btn-full" onClick={() => handleAdd(true)}>
              Add & go to cart → ({cartCount})
            </button>
          )}
          <div style={{ marginTop:12, fontSize:11, color:'var(--text3)', textAlign:'center' }}>
            Adding again from products page creates a separate line item
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
//  CART & SHIPPING
// ═══════════════════════════════════════════════════════════════
export function CartScreen() {
  const navigate  = useNavigate();
  const { toast } = useToast();
  const { company } = useAuth();
  const isDirectWholesale = company?.billingModel === 'direct_wholesale';
  const { items, subtotal, coupon, couponDiscount, volumeDiscountTotal,
    updateQty, removeItem, customer, setCustomer,
    address, setAddress, setRates, selectRate, setTax,
    roomNumber, setRoomNumber, setCoupon, clearCoupon, clearCart } = useCart();

  const [couponInput,      setCouponInput]      = useState('');
  const [couponLoading,    setCouponLoading]    = useState(false);
  const [couponError,      setCouponError]      = useState('');
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [addrWarning,      setAddrWarning]      = useState(null);
  const [addrSuggested,    setAddrSuggested]    = useState(null);
  const [validating,       setValidating]       = useState(false);
  const autocompleteRef    = useRef(null);
  const address1InputRef   = useRef(null);

  const handleAddrChange = (fields) => {
    setAddress(fields);
    setAddrWarning(null);
    setAddrSuggested(null);
  };

  const handleCountryChange = (code) => {
    setAddress({ country: code, province: '' });
    setAddrWarning(null);
    setAddrSuggested(null);
    if (coupon?.domesticOnly && code !== 'US' && code !== 'CA') {
      clearCoupon();
      toast('Coupon removed — only valid for US and Canada orders', 'error');
    }
  };

  // Initialize Google Places autocomplete on address1 field
  useEffect(() => {
    const tryInit = () => {
      if (!window.google?.maps?.places?.Autocomplete || !address1InputRef.current) return false;
      try {
        const ac = new window.google.maps.places.Autocomplete(address1InputRef.current, {
          types: ['address'],
          fields: ['address_components'],
        });
        ac.addListener('place_changed', () => {
          const place = ac.getPlace();
          if (!place.address_components) return;
          const get = (type) => place.address_components.find(c => c.types.includes(type))?.long_name || '';
          const getShort = (type) => place.address_components.find(c => c.types.includes(type))?.short_name || '';
          const streetNumber = get('street_number');
          const route        = get('route');
          const subpremise   = get('subpremise');
          const street1      = subpremise ? `${subpremise} - ${streetNumber} ${route}` : `${streetNumber} ${route}`;
          const city         = get('locality') || get('sublocality_level_1') || get('sublocality') || get('postal_town') || get('administrative_area_level_2');
          const province     = getShort('administrative_area_level_1');
          const zip          = get('postal_code');
          const country      = getShort('country');
          setAddress({ address1: street1.trim(), city, province, zip, country });
          setAddrWarning(null);
          setAddrSuggested(null);
        });
        autocompleteRef.current = ac;
        return true;
      } catch (err) {
        console.warn('Google Places init failed:', err.message);
        return false;
      }
    };
    if (!tryInit()) {
      const interval = setInterval(() => { if (tryInit()) clearInterval(interval); }, 500);
      return () => clearInterval(interval);
    }
  }, []);

  const handleClearCart = () => {
    clearCart();
    setShowClearConfirm(false);
    setRates([]);
    navigate('/products');
  };

  const handleApplyCoupon = async () => {
    if (!couponInput.trim()) return;
    setCouponLoading(true); setCouponError('');
    try {
      const { data } = await validateCoupon(couponInput.trim(), subtotal, address.country);
      setCoupon({ ...data.coupon, discountAmount: 0 });
      setCouponInput('');
      toast(`Coupon "${data.coupon.code}" applied!`, 'success');
    } catch (err) {
      setCouponError(err.response?.data?.error || 'Invalid coupon code');
    } finally { setCouponLoading(false); }
  };

  const addrReady = address.address1?.trim() && address.city?.trim() &&
    (address.country !== 'US' || (address.province && address.zip)) &&
    (address.country !== 'CA' || address.province);

  const canContinue = addrReady && customer.firstName?.trim() && (isDirectWholesale || roomNumber?.trim());

  const handleContinue = async () => {
    setValidating(true);
    setAddrWarning(null);
    setAddrSuggested(null);
    try {
      const { data: addrResult } = await validateAddress({
        firstName: customer.firstName,
        lastName:  customer.lastName,
        address1:  address.address1,
        address2:  address.address2,
        city:      address.city,
        province:  address.province,
        zip:       address.zip,
        country:   address.country,
      });
      if (!addrResult.valid && !addrResult.skipped) {
        setAddrWarning(addrResult.message || 'Address could not be verified');
        setAddrSuggested(addrResult.suggested || null);
        setValidating(false);
        return;
      }
    } catch (_) {
      // Fail open — if ShipStation unavailable, proceed anyway
    }
    setValidating(false);
    navigate('/finalize');
  };

  if (!items.length) return (
    <div className="page">
      <div className="empty" style={{ paddingTop:80 }}>
        <div className="empty-icon">🛒</div>
        <div className="empty-title">Your cart is empty</div>
        <button className="btn btn-primary" style={{ marginTop:16 }} onClick={() => navigate('/products')}>Browse products →</button>
      </div>
    </div>
  );

  return (
    <div className="page">
      <div className="page-hdr">
        <div className="page-title">Cart</div>
        <button className="btn btn-sm btn-danger" onClick={() => setShowClearConfirm(true)}>Clear Cart</button>
      </div>

      {showClearConfirm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:'#fff', borderRadius:12, padding:28, width:340, boxShadow:'0 8px 32px rgba(0,0,0,0.15)' }}>
            <div style={{ fontSize:16, fontWeight:600, marginBottom:8 }}>Clear cart?</div>
            <div style={{ fontSize:14, color:'#555', marginBottom:20 }}>This will remove all items and customer info. Ok to proceed?</div>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button className="btn" onClick={() => setShowClearConfirm(false)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleClearCart}>Yes, clear cart</button>
            </div>
          </div>
        </div>
      )}
      <Steps steps={STEPS} current={1} />
      <div className="sidebar">
        <div>
          <div className="card" style={{ marginBottom:16 }}>
            <div style={{ fontWeight:600, marginBottom:14 }}>Items ({items.length})</div>
            {items.map((item, idx) => (
              <div key={item.id} style={{ borderBottom:'1px solid var(--border)', paddingBottom:12, marginBottom:12 }}>
                <div style={{ display:'flex', gap:10, marginBottom:8 }}>
                  {item.imageUrl && <img src={item.imageUrl} alt={item.productName} style={{ width:48, height:48, objectFit:'contain', borderRadius:'var(--radius)', flexShrink:0, background:'var(--bg)' }} />}
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:500 }}>{item.productName}</div>
                    <div style={{ fontSize:11, color:'var(--text3)' }}>SKU: {item.sku} · Line {idx+1}</div>
                  </div>
                  <div style={{ textAlign:'right', flexShrink:0 }}>
                    <div style={{ fontSize:12, color:'var(--text2)' }}>${item.retailPrice.toFixed(2)} × {item.quantity}</div>
                    <div style={{ fontWeight:600 }}>${(item.retailPrice*item.quantity).toFixed(2)}</div>
                  </div>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <Qty value={item.quantity} onChange={q => updateQty(item.id, q)} />
                  <button className="btn btn-danger btn-sm" onClick={() => removeItem(item.id)}>Remove</button>
                </div>
              </div>
            ))}
            <button className="btn btn-sm" onClick={() => navigate('/products')}>+ Add more items</button>
          </div>

          <div className="card" style={{ marginBottom:16 }}>
            <div style={{ fontWeight:600, marginBottom:14 }}>Customer & Room</div>
            <div className="grid2">
              <div className="field"><label className="label">First name *</label><input className="input" value={customer.firstName} onChange={e => setCustomer({ firstName: e.target.value })} /></div>
              <div className="field"><label className="label">Last name *</label><input className="input" value={customer.lastName} onChange={e => setCustomer({ lastName: e.target.value })} /></div>
            </div>
            <div className="grid2">
              <div className="field"><label className="label">Email</label><input className="input" type="email" value={customer.email} onChange={e => setCustomer({ email: e.target.value })} /></div>
              <div className="field"><label className="label">Phone</label><input className="input" value={customer.phone} onChange={e => setCustomer({ phone: e.target.value })} /></div>
            </div>
            {!isDirectWholesale && (
              <div className="field">
                <label className="label">Room # (applies to all items)</label>
                <input className="input" value={roomNumber} onChange={e => setRoomNumber(e.target.value)} placeholder="e.g. Room 12B, Table 5, Booth A" />
              </div>
            )}
          </div>

          <div className="card">
            <div style={{ fontWeight:600, marginBottom:14 }}>Shipping address</div>
            <div className="field"><label className="label">Country</label>
              <CountrySelect value={address.country || 'US'} onChange={handleCountryChange} />
            </div>
            <div className="field"><label className="label">Address line 1</label><input ref={address1InputRef} className="input" value={address.address1} onChange={e => handleAddrChange({ address1: e.target.value })} placeholder="123 Main St or APT 302 - 123 Main St" /></div>
            <div className="field"><label className="label">Address line 2</label><input className="input" value={address.address2} onChange={e => handleAddrChange({ address2: e.target.value })} placeholder="Apt, suite, unit (optional)" /></div>
            <div className="grid2">
              <div className="field"><label className="label">City</label><input className="input" value={address.city} onChange={e => handleAddrChange({ city: e.target.value })} /></div>
              <div className="field"><label className="label">ZIP / Postal code</label><input className="input" value={address.zip} onChange={e => handleAddrChange({ zip: e.target.value })} /></div>
            </div>
            {address.country === 'US' && (
              <div className="field"><label className="label">State / Province</label>
                <select className="select" value={address.province} onChange={e => handleAddrChange({ province: e.target.value })}>
                  <option value="">Select state</option>
                  {US_STATES.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
            )}
            {address.country === 'CA' && (
              <div className="field"><label className="label">State / Province</label>
                <select className="select" value={address.province} onChange={e => handleAddrChange({ province: e.target.value })}>
                  <option value="">Select province</option>
                  {CA_PROVINCES.map(p => <option key={p}>{p}</option>)}
                </select>
              </div>
            )}
            {address.country !== 'US' && address.country !== 'CA' && (
              <div className="field"><label className="label">State / Province (optional)</label>
                <input className="input" value={address.province} onChange={e => handleAddrChange({ province: e.target.value })} placeholder="Optional" />
              </div>
            )}
          </div>
        </div>

        <div>
          <div className="card" style={{ marginBottom:14 }}>
            <div style={{ fontWeight:600, marginBottom:10 }}>Order Summary</div>
            <div className="sum-row"><span>Subtotal</span><span>{fmtMoney(subtotal)}</span></div>
            {volumeDiscountTotal > 0 && (
              <div className="sum-row" style={{ color:'var(--success)' }}>
                <span>Volume Discount</span><span>-{fmtMoney(volumeDiscountTotal)}</span>
              </div>
            )}
            {coupon && couponDiscount > 0 && (
              <div className="sum-row" style={{ color:'var(--success)' }}>
                <span>Discount ({coupon.code})</span><span>-{fmtMoney(couponDiscount)}</span>
              </div>
            )}
            <div style={{ fontSize:12, color:'var(--text3)', marginTop:6 }}>Shipping & tax calculated at next step</div>
            {/* Coupon input */}
            <div style={{ marginTop:12 }}>
              {coupon ? (
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', background:'var(--success-bg)', border:'1px solid var(--success-bd)', borderRadius:'var(--radius)', padding:'8px 12px', fontSize:13 }}>
                  <span style={{ color:'var(--success)' }}>✅ Coupon <strong>{coupon.code}</strong> applied</span>
                  <button className="btn btn-sm" onClick={() => clearCoupon()} style={{ fontSize:11 }}>Remove</button>
                </div>
              ) : (
                <>
                  <div style={{ display:'flex', gap:6 }}>
                    <input className="input" style={{ flex:1, fontSize:13 }} placeholder="Coupon code"
                      value={couponInput} onChange={e => { setCouponInput(e.target.value.toUpperCase()); setCouponError(''); }}
                      onKeyDown={e => e.key === 'Enter' && handleApplyCoupon()} />
                    <button className="btn btn-primary btn-sm" disabled={couponLoading || !couponInput.trim()} onClick={handleApplyCoupon}>
                      {couponLoading ? <Spinner size={14} /> : 'Apply'}
                    </button>
                  </div>
                  {couponError && <div style={{ color:'var(--danger)', fontSize:12, marginTop:4 }}>{couponError}</div>}
                </>
              )}
            </div>
          </div>
          <div className="alert alert-warn" style={{ marginBottom:12 }}>No payment collected. Order saved as pending.</div>
          <button className="btn btn-primary btn-full btn-lg" disabled={!canContinue || validating}
            onClick={handleContinue}>
            {validating ? 'Validating address…' : 'Continue to finalize →'}
          </button>
          {!customer.firstName?.trim() && <div style={{ fontSize:11, color:'var(--danger)', textAlign:'center', marginTop:8 }}>First name is required</div>}
          {!isDirectWholesale && !roomNumber?.trim() && customer.firstName?.trim() && <div style={{ fontSize:11, color:'var(--danger)', textAlign:'center', marginTop:8 }}>Room # is required</div>}
          {!addrReady && customer.firstName?.trim() && (isDirectWholesale || roomNumber?.trim()) && <div style={{ fontSize:11, color:'var(--danger)', textAlign:'center', marginTop:8 }}>Please complete the shipping address</div>}

          {/* Address validation warning */}
          {addrWarning && (
            <div style={{ marginTop:12, padding:'12px 14px', background:'#FEF3C7', border:'1px solid #F59E0B', borderRadius:8 }}>
              <div style={{ fontSize:13, fontWeight:500, color:'#92400E', marginBottom:6 }}>⚠️ Address could not be verified</div>
              <div style={{ fontSize:13, color:'#78350F', marginBottom:8 }}>{addrWarning}</div>
              {addrSuggested && (
                <div style={{ marginBottom:8 }}>
                  <div style={{ fontSize:12, color:'#92400E', marginBottom:4 }}>Suggested correction:</div>
                  <div style={{ fontSize:13, color:'#78350F', fontWeight:500 }}>
                    {addrSuggested.address1}{addrSuggested.address2 ? ', ' + addrSuggested.address2 : ''}<br/>
                    {addrSuggested.city}, {addrSuggested.province} {addrSuggested.zip}, {addrSuggested.country}
                  </div>
                  <button className="btn btn-sm" style={{ marginTop:6, background:'#F59E0B', color:'#fff', border:'none' }}
                    onClick={() => { setAddress({ ...address, ...addrSuggested }); setAddrWarning(null); setAddrSuggested(null); }}>
                    Use suggested address
                  </button>
                </div>
              )}
              <button className="btn btn-sm" style={{ fontSize:12, color:'#92400E' }}
                onClick={() => { setAddrWarning(null); setAddrSuggested(null); navigate('/finalize'); }}>
                Continue with current address anyway
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
//  FINALIZE
// ═══════════════════════════════════════════════════════════════
function FinalizeScreenInner() {
  const navigate  = useNavigate();
  const { toast } = useToast();
  const { company } = useAuth();
  const { items, subtotal, shippingCost, taxAmount, tax, total, coupon, couponDiscount, volumeDiscountTotal,
    customer, address, setAddress, shipping, setRates, selectRate, setTax,
    roomNumber, clearCart, clearCoupon, setCoupon } = useCart();
  const isDirectWholesale = company?.billingModel === 'direct_wholesale';

  // Redirect if cart is empty
  useEffect(() => {
    if (!items.length) navigate('/products');
  }, []);

  const [refs,          setRefs]          = useState(() => items.reduce((a,i) => ({ ...a, [i.id]: { referenceNumber:'', referenceNumber2:'' } }), {}));
  const [saving,        setSaving]        = useState(false);
  const [agreed,        setAgreed]        = useState(false);
  const [couponInput,   setCouponInput]   = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError,   setCouponError]   = useState('');
  const [ratesLoading,  setRatesLoading]  = useState(false);

  // Calculate flat rate shipping on mount, and tax on mount + whenever the
  // coupon changes (applying or removing it here changes the taxable amount).
  // Tax calculation itself is unified server-side now — same endpoint for
  // both billing models; the backend decides whether Stripe Tax applies
  // (direct_wholesale always, invoice_retail only if "Enable tax" is checked)
  // and falls back to the group's flat rate if Stripe Tax fails.
  useEffect(() => {
    if (!address.country) return;
    setRatesLoading(true);
    const isDomesticRate = address.country === 'US' || address.country === 'CA';

    getGlobalSettings().then(({ data: settings }) => {
      const freeShipping = !!company?.freeShippingEnabled;
      const rates = freeShipping
        ? (isDomesticRate
            ? [{ id:'free_domestic', title:'Free Shipping', price: 0 }]
            : [{ id:'free_intl', title:'International Shipping', price: settings.freeShippingInternationalRate }])
        : (isDomesticRate
            ? [{ id:'std', title:'Standard Shipping', price: settings.standardDomesticShippingRate }]
            : [{ id:'intl_std', title:'International Shipping', price: settings.standardInternationalShippingRate }]);
      setRates(rates);
      selectRate(rates[0]);

      // Coupon is passed through so a percent-discount reduces the taxable
      // amount the same way it reduces the subtotal — otherwise tax would be
      // calculated on the pre-discount price.
      calculateStripeTax({
        items: items.map(i => ({ retailPrice: i.retailPrice, quantity: i.quantity, sku: i.sku })),
        shippingAddress: address,
        shippingAmount: rates[0].price,
        coupon: coupon ? { type: coupon.type, discountPercent: coupon.discountPercent } : null,
      })
        .then(r => setTax({
          enabled: r.data.applicable, rate: 0, amount: r.data.taxAmount,
          taxCalculationId: r.data.taxCalculationId, taxAddressHash: r.data.taxAddressHash,
          taxFallbackUsed: r.data.taxFallbackUsed || false,
        }))
        .catch(() => setTax({ enabled: false, rate: 0, amount: 0 }))
        .finally(() => setRatesLoading(false));
    }).catch(() => setRatesLoading(false));
  }, [coupon]);

  const handleApplyCoupon = async () => {
    if (!couponInput.trim()) return;
    setCouponLoading(true); setCouponError('');
    try {
      const { data } = await validateCoupon(couponInput.trim(), subtotal, address.country);
      setCoupon({ ...data.coupon });
      setCouponInput('');
      toast(`Coupon "${data.coupon.code}" applied!`, 'success');
    } catch (err) {
      setCouponError(err.response?.data?.error || 'Invalid coupon code');
    } finally { setCouponLoading(false); }
  };

  const allFilled = items.every(i => refs[i.id]?.referenceNumber.trim());

  const handleSave = async () => {
    if (!allFilled) { toast('Enter reference # for all items', 'error'); return; }
    setSaving(true);
    try {
      const payload = {
        customer: { firstName: customer.firstName, lastName: customer.lastName, email: customer.email, phone: customer.phone },
        shippingAddress: { ...address, firstName: customer.firstName, lastName: customer.lastName },
        roomNumber,
        items: items.map(i => ({
          productId:        i.productId,
          productName:      i.productName,
          imageUrl:         i.imageUrl || '',
          sku:              i.sku,
          quantity:         i.quantity,
          retailPrice:      i.retailPrice,
          wholesalePrice:   i.wholesalePrice || 0,
          lineTotal:        i.retailPrice * i.quantity,
          referenceNumber:  refs[i.id]?.referenceNumber  || '',
          referenceNumber2: refs[i.id]?.referenceNumber2 || '',
        })),
        pricing: {
          subtotal,
          shippingCost,
          shippingTitle: shipping.selected?.title || '',
          taxAmount,
          total,
        },
        coupon: coupon ? { code: coupon.code, type: coupon.type, discountPercent: coupon.discountPercent } : null,
        taxCalculationId: tax.taxCalculationId || '',
        taxFallbackUsed:  tax.taxFallbackUsed || false,
        taxAddressHash:   tax.taxAddressHash   || '',
      };
      await createOrder(payload);
      toast('Sale saved!', 'success');
      clearCart();
      navigate('/pending');
    } catch (err) {
      toast(err.response?.data?.error || 'Failed to save order', 'error');
    } finally { setSaving(false); }
  };

  return (
    <div className="page">
      <div className="page-hdr"><div className="page-title">Finalize Sale</div></div>
      <Steps steps={STEPS} current={2} />
      <div className="sidebar">
        <div>
          <div className="card" style={{ marginBottom:16 }}>
            <div style={{ fontWeight:600, marginBottom:4 }}>Photo reference numbers</div>
            {!isDirectWholesale && (
              <div style={{ fontSize:12, color:'var(--text2)', marginBottom:12 }}>
                Room: <strong>{roomNumber || '—'}</strong> (applies to all items)
              </div>
            )}
            {items.map((item, idx) => (
              <div key={item.id} style={{
                border:`1px solid ${refs[item.id]?.referenceNumber ? 'var(--success-bd)' : 'var(--border)'}`,
                borderRadius:'var(--radius)', padding:14, marginBottom:12,
              }}>
                <div style={{ display:'flex', gap:10, marginBottom:10 }}>
                  {item.imageUrl && <img src={item.imageUrl} alt="" style={{ width:40, height:40, objectFit:'contain', borderRadius:4, background:'var(--bg)', flexShrink:0 }} />}
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:500 }}>{item.productName} × {item.quantity}</div>
                    <div style={{ fontSize:11, color:'var(--text3)' }}>Line {idx+1} · SKU: {item.sku}</div>
                  </div>
                  <div style={{ fontWeight:600 }}>${(item.retailPrice*item.quantity).toFixed(2)}</div>
                </div>
                <div className="field" style={{ marginBottom:6 }}>
                  <label className="label">Front photo reference # *</label>
                  <input className="input" placeholder="e.g. IMG_4821" value={refs[item.id]?.referenceNumber||''}
                    onChange={e => setRefs(p => ({ ...p, [item.id]: { ...p[item.id], referenceNumber: e.target.value } }))} />
                </div>
                <div className="field" style={{ marginBottom:0 }}>
                  <label className="label" style={{ color:'var(--text2)' }}>Back photo reference # <span style={{ fontWeight:400, color:'var(--text3)' }}>(optional)</span></label>
                  <input className="input" placeholder="e.g. IMG_4822 — leave blank if not needed"
                    value={refs[item.id]?.referenceNumber2||''}
                    onChange={e => setRefs(p => ({ ...p, [item.id]: { ...p[item.id], referenceNumber2: e.target.value } }))} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="card" style={{ marginBottom:14 }}>
            <div style={{ fontWeight:600, marginBottom:10 }}>Summary</div>
            <div className="sum-row"><span>Customer</span><span>{customer.firstName} {customer.lastName}</span></div>
            {!isDirectWholesale && <div className="sum-row"><span>Room #</span><span>{roomNumber || '—'}</span></div>}
            <div className="sum-row"><span>Ship to</span><span>{address.city}, {address.province}</span></div>
            <div className="sum-row"><span>Items</span><span>{items.length} line items</span></div>
            <hr className="divider" />
            <div className="sum-row"><span>Subtotal</span><span>{fmtMoney(subtotal)}</span></div>
            {volumeDiscountTotal > 0 && (
              <div className="sum-row" style={{ color:'var(--success)' }}>
                <span>Volume Discount</span><span>-{fmtMoney(volumeDiscountTotal)}</span>
              </div>
            )}
            {ratesLoading ? (
              <div className="sum-row"><span>Shipping</span><span><Spinner size={12} /></span></div>
            ) : (
              <div className="sum-row">
                <span>Shipping{shipping.selected?.title ? ` (${shipping.selected.title})` : ''}</span>
                <span>
                  {coupon?.type === 'free_shipping'
                    ? <><span style={{ textDecoration:'line-through', color:'var(--text3)', marginRight:4 }}>{fmtMoney(shippingCost + couponDiscount)}</span>FREE</>
                    : fmtMoney(shippingCost)}
                </span>
              </div>
            )}
            {coupon && couponDiscount > 0 && coupon.type !== 'free_shipping' && (
              <div className="sum-row" style={{ color:'var(--success)' }}>
                <span>Discount ({coupon.code})</span>
                <span>-{fmtMoney(couponDiscount)}</span>
              </div>
            )}
            {tax.enabled && <div className="sum-row"><span>Tax</span><span>{fmtMoney(taxAmount)}</span></div>}
            <hr className="divider" />
            <div className="sum-total"><span>Total</span><span>{fmtMoney(total)}</span></div>

            {/* Coupon input */}
            <div style={{ marginTop:12 }}>
              {coupon ? (
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', background:'var(--success-bg)', border:'1px solid var(--success-bd)', borderRadius:'var(--radius)', padding:'8px 12px', fontSize:13 }}>
                  <span style={{ color:'var(--success)' }}>✅ Coupon <strong>{coupon.code}</strong> applied</span>
                  <button className="btn btn-sm" onClick={() => clearCoupon()} style={{ fontSize:11 }}>Remove</button>
                </div>
              ) : (
                <>
                  <div style={{ display:'flex', gap:6 }}>
                    <input className="input" style={{ flex:1, fontSize:13 }} placeholder="Coupon code"
                      value={couponInput} onChange={e => { setCouponInput(e.target.value.toUpperCase()); setCouponError(''); }}
                      onKeyDown={e => e.key === 'Enter' && handleApplyCoupon()} />
                    <button className="btn btn-primary btn-sm" disabled={couponLoading || !couponInput.trim()} onClick={handleApplyCoupon}>
                      {couponLoading ? <Spinner size={14} /> : 'Apply'}
                    </button>
                  </div>
                  {couponError && <div style={{ color:'var(--danger)', fontSize:12, marginTop:4 }}>{couponError}</div>}
                </>
              )}
            </div>
          </div>

          {isDirectWholesale ? (
            <div className="alert alert-info" style={{ marginBottom:12 }}>
              💳 Card details will be collected on the next screen once photos are uploaded — no charge yet.
            </div>
          ) : (
            <div className="alert alert-warn" style={{ marginBottom:12 }}>No payment collected. Sale saved as <strong>Pending</strong>.</div>
          )}

          {/* Disclaimer checkbox */}
          <div style={{ background:'var(--bg)', border:'1px solid var(--border2)', borderRadius:'var(--radius)', padding:12, marginBottom:12 }}>
            <label style={{ display:'flex', gap:10, cursor:'pointer', alignItems:'flex-start' }}>
              <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)}
                style={{ marginTop:2, width:16, height:16, flexShrink:0, cursor:'pointer' }} />
              {company?.orderDisclaimer ? (
                <span style={{ fontSize:12, color:'var(--text2)', lineHeight:1.6 }} className="rich-content"
                  dangerouslySetInnerHTML={{ __html: company.orderDisclaimer }} />
              ) : (
                <span style={{ fontSize:12, color:'var(--text2)', lineHeight:1.6 }}>
                  By placing this order you agree to our{' '}
                  <a href="/terms" target="_blank" style={{ color:'var(--brand)' }}>terms and conditions</a>.
                  You understand and accept that we share your order information with Solidiphy, Inc to fulfill this order.
                </span>
              )}
            </label>
          </div>

          <button className="btn btn-primary btn-full btn-lg"
            disabled={!allFilled || !agreed || saving || ratesLoading}
            onClick={handleSave}>
            {saving ? 'Saving…' : 'Complete Sale → Awaiting Photo'}
          </button>
          {!allFilled && <div style={{ fontSize:11, color:'var(--text3)', textAlign:'center', marginTop:8 }}>All reference numbers required</div>}
          {!agreed && allFilled && <div style={{ fontSize:11, color:'var(--danger)', textAlign:'center', marginTop:8 }}>Please accept the terms and conditions</div>}
        </div>
      </div>
    </div>
  );
}

// Public export — Finalize no longer touches Stripe at all (moved to the
// Upload screen, see UploadScreen's export below for the Elements wrapper).
export function FinalizeScreen() {
  return <FinalizeScreenInner />;
}

// ═══════════════════════════════════════════════════════════════
//  PENDING SALES
// ═══════════════════════════════════════════════════════════════
export function PendingScreen() {
  const navigate  = useNavigate();
  const { toast } = useToast();
  const [orders,  setOrders]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [status,  setStatus]  = useState('active');

  const load = async () => {
    setLoading(true);
    try { const { data } = await getOrders({ search, status }); setOrders(data.orders); }
    catch { setOrders([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [search, status]);
  useEffect(() => { load(); }, []);

  return (
    <div className="page">
      <div className="page-hdr">
        <div><div className="page-title">Pending Sales</div><div className="page-sub">Orders awaiting photo upload</div></div>
      </div>
      <div style={{ display:'flex', gap:10, marginBottom:20 }}>
        <input className="input" style={{ flex:1 }} placeholder="Search by customer, order #…" value={search} onChange={e => setSearch(e.target.value)} />
        <select className="select" style={{ width:180 }} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="active">Active (default)</option>
          <option value="awaiting_photo">Awaiting Photo</option>
          <option value="in_process">In Process</option>
        </select>
      </div>
      {loading ? <Loading label="Loading orders…" /> : orders.length === 0
        ? <Empty icon="📋" title="No orders found" />
        : (
          <div className="card" style={{ padding:0, overflow:'hidden' }}>
            <table className="tbl">
              <thead><tr>
                <th>Customer / Order</th><th>Room #</th><th>Date</th><th>Group</th><th style={{ textAlign:'right' }}>Total</th><th>Status</th><th></th>
              </tr></thead>
              <tbody>
                {orders.map(order => (
                  <tr key={order.id}
                    onClick={() => order.status === 'awaiting_photo' && navigate(`/upload/${order.id}`)}
                    style={{ cursor: order.status === 'awaiting_photo' ? 'pointer' : 'default' }}>
                    <td>
                      <div style={{ fontWeight:500 }}>{order.customerFirstName} {order.customerLastName}</div>
                      <div style={{ fontSize:11, color:'var(--text3)' }}>{order.orderNumber} · {order.items?.length} items</div>
                    </td>
                    <td style={{ fontSize:12, color:'var(--text2)' }}>{order.roomNumber || '—'}</td>
                    <td style={{ color:'var(--text2)' }}>{fmtDate(order.createdAt)}</td>
                    <td><span style={{ background:'var(--brand-light)', color:'var(--brand)', padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:500 }}>{order.groupName}</span></td>
                    <td style={{ textAlign:'right', fontWeight:600 }}>{fmtMoney(order.total)}</td>
                    <td><Badge status={order.status} /></td>
                    <td>
                      {order.status === 'awaiting_photo' && (
                        <button className="btn btn-primary btn-sm" onClick={e => { e.stopPropagation(); navigate(`/upload/${order.id}`); }}>
                          Upload Photo
                        </button>
                      )}
                      {order.status === 'in_process' && (
                        <span style={{ fontSize:11, color:'var(--text3)', fontStyle:'italic' }}>In Process</span>
                      )}
                      {!['awaiting_photo','in_process'].includes(order.status) && (
                        <span style={{ fontSize:11, color:'var(--text3)', fontStyle:'italic' }}>In progress</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
//  UPLOAD PHOTOS
// ═══════════════════════════════════════════════════════════════
function UploadScreenInner() {
  const navigate   = useNavigate();
  const { id }     = useParams();
  const { toast }  = useToast();
  const { company } = useAuth();
  const stripe   = useStripe();
  const elements = useElements();
  const isDirectWholesale = company?.billingModel === 'direct_wholesale';

  const [order,      setOrder]      = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [photos,     setPhotos]     = useState({});
  const [working,    setWorking]    = useState({});
  const [fulfilling, setFulfilling] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [billingSameAsShipping, setBillingSameAsShipping] = useState(true);
  const [billingAddress, setBillingAddress] = useState({ address1:'', address2:'', city:'', province:'', zip:'', country:'US' });
  const billingAddress1Ref = useRef(null);

  // Google Places autocomplete on the billing address1 field — only relevant
  // once the field is actually rendered (billingSameAsShipping === false).
  useEffect(() => {
    if (billingSameAsShipping) return;
    const tryInit = () => {
      if (!window.google?.maps?.places?.Autocomplete || !billingAddress1Ref.current) return false;
      try {
        const ac = new window.google.maps.places.Autocomplete(billingAddress1Ref.current, {
          types: ['address'],
          fields: ['address_components'],
        });
        ac.addListener('place_changed', () => {
          const place = ac.getPlace();
          if (!place.address_components) return;
          const get = (type) => place.address_components.find(c => c.types.includes(type))?.long_name || '';
          const getShort = (type) => place.address_components.find(c => c.types.includes(type))?.short_name || '';
          const streetNumber = get('street_number');
          const route        = get('route');
          const subpremise    = get('subpremise');
          const street1       = subpremise ? `${subpremise} - ${streetNumber} ${route}` : `${streetNumber} ${route}`;
          const city          = get('locality') || get('sublocality_level_1') || get('sublocality') || get('postal_town') || get('administrative_area_level_2');
          const province      = getShort('administrative_area_level_1');
          const zip           = get('postal_code');
          const country       = getShort('country');
          setBillingAddress({ address1: street1.trim(), address2:'', city, province, zip, country });
        });
        return true;
      } catch (err) {
        console.warn('Google Places init failed (billing):', err.message);
        return false;
      }
    };
    if (!tryInit()) {
      const interval = setInterval(() => { if (tryInit()) clearInterval(interval); }, 500);
      return () => clearInterval(interval);
    }
  }, [billingSameAsShipping]);

  const refresh = useCallback(async () => {
    try { const { data } = await getOrder(id); setOrder(data.order); } catch {}
  }, [id]);

  useEffect(() => {
    setLoading(true);
    refresh().finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (order) {
      const ex = {};
      order.items?.forEach(i => {
        ex[i.id] = {
          front: i.photoUrl ? { name: i.photoOneDrivePath || 'Uploaded', preview: i.photoUrl, uploaded: true } : null,
          back:  i.photoUrl2 ? { name: i.photoOneDrivePath2 || 'Uploaded', preview: i.photoUrl2, uploaded: true } : null,
          backDisabled: i.backPhotoDisabled || false,
        };
      });
      setPhotos(ex);
    }
  }, [order]);

  const handleFileSelect = (item, file, isBack) => {
    if (!file) return;
    const preview = URL.createObjectURL(file);
    setPhotos(p => ({
      ...p,
      [item.id]: {
        ...p[item.id],
        [isBack ? 'back' : 'front']: { file, preview, name: file.name, uploaded: false },
      }
    }));
    toast(`${file.name} selected`, 'success');
  };

  const toggleBackDisabled = (item) => {
    setPhotos(p => ({
      ...p,
      [item.id]: { ...p[item.id], backDisabled: !p[item.id]?.backDisabled }
    }));
  };

  const isItemComplete = (item) => {
    const p = photos[item.id];
    const needsBack = item.referenceNumber2 && !p?.backDisabled;
    return !!p?.front && (!needsBack || !!p?.back);
  };

  const billingReady = billingSameAsShipping ||
    (billingAddress.address1?.trim() && billingAddress.city?.trim() && billingAddress.zip?.trim());

  const handleSendToProduction = async () => {
    setFulfilling(true);
    setPaymentError('');
    try {
      await Promise.all(order.items.map(async (item) => {
        const p = photos[item.id];
        // Upload front photo if not already uploaded
        if (p?.front && !p.front.uploaded) {
          const fd = new FormData();
          fd.append('photo', p.front.file);
          fd.append('orderId', order.id);
          fd.append('itemId', item.id);
          fd.append('isBackPhoto', 'false');
          const { data } = await uploadPhoto(fd);
          await linkPhoto(order.id, item.id, { photoUrl: data.file.photoUrl, photoOneDrivePath: data.file.photoOneDrivePath });
        }
        // Upload back photo if exists and not disabled
        const needsBack = item.referenceNumber2 && !p?.backDisabled;
        if (needsBack && p?.back && !p.back.uploaded) {
          const fd2 = new FormData();
          fd2.append('photo', p.back.file);
          fd2.append('orderId', order.id);
          fd2.append('itemId', item.id);
          fd2.append('isBackPhoto', 'true');
          const { data: data2 } = await uploadPhoto(fd2);
          await linkPhoto(order.id, item.id, { photoUrl2: data2.file.photoUrl2, photoOneDrivePath2: data2.file.photoOneDrivePath2, isBackPhoto: true });
        }
        // If back disabled, update flag on server
        if (item.referenceNumber2 && p?.backDisabled) {
          await linkPhoto(order.id, item.id, { backPhotoDisabled: true, isBackPhotoDisabledUpdate: true });
        }
      }));

      // ── Phase 7: direct_wholesale charges the card right here, on-session ───
      // Phase 9: a $0 order (100% discount/coupon + free shipping) skips Stripe
      // entirely — the backend marks it paymentStatus:'waived' and there's
      // nothing to charge, so no card form is needed at all.
      const needsPaymentNow = isDirectWholesale && !['captured', 'waived'].includes(order.paymentStatus) && order.total > 0;
      if (needsPaymentNow) {
        if (!stripe || !elements) { toast('Payment form still loading, please wait', 'error'); setFulfilling(false); return; }
        if (!billingReady) { toast('Enter the billing address', 'error'); setFulfilling(false); return; }

        const billingAddr = billingSameAsShipping ? order.shippingAddress1 ? {
          address1: order.shippingAddress1, address2: order.shippingAddress2,
          city: order.shippingCity, province: order.shippingProvince,
          zip: order.shippingZip, country: order.shippingCountry,
        } : {} : billingAddress;

        const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
          type: 'card',
          card: elements.getElement(CardElement),
          billing_details: {
            name:  `${order.customerFirstName} ${order.customerLastName}`,
            email: order.customerEmail || undefined,
            address: {
              line1:       billingAddr.address1 || undefined,
              line2:       billingAddr.address2 || undefined,
              city:        billingAddr.city     || undefined,
              state:       billingAddr.province  || undefined,
              postal_code: billingAddr.zip       || undefined,
              country:     billingAddr.country   || 'US',
            },
          },
        });
        if (pmError) {
          setPaymentError(pmError.message || 'Card details invalid — please check and try again.');
          setFulfilling(false);
          return;
        }

        let fulfillResult;
        try {
          const { data } = await fulfillOrder(order.id, { paymentMethodId: paymentMethod.id });
          fulfillResult = data;
        } catch (err) {
          const respData = err.response?.data;
          if (respData?.requiresAction && respData?.clientSecret) {
            // 3D Secure — complete the extra authentication step right here,
            // then call fulfill again; the backend's retry path picks up the
            // same PaymentIntent and sees it's already succeeded.
            const { error: confirmError } = await stripe.confirmCardPayment(respData.clientSecret);
            if (confirmError) {
              setPaymentError(confirmError.message || 'Card authentication failed — please try again.');
              setFulfilling(false);
              return;
            }
            const { data: retryData } = await fulfillOrder(order.id, { paymentMethodId: paymentMethod.id });
            fulfillResult = retryData;
          } else {
            // Declined or otherwise failed — nothing advanced, safe to retry with a different card.
            setPaymentError(respData?.error || 'Payment failed. Please try a different card.');
            setFulfilling(false);
            return;
          }
        }
      } else {
        await fulfillOrder(order.id);
      }

      toast('Order is now In Process!', 'success');
      navigate('/pending');
    } catch (err) {
      toast(err.response?.data?.error || 'Failed', 'error');
    } finally { setFulfilling(false); }
  };

  if (loading) return <div className="page"><Loading label="Loading order…" /></div>;
  if (!order)  return <div className="page"><p>Order not found.</p></div>;

  const allDone   = order.items?.every(i => isItemComplete(i));
  const doneCount = order.items?.filter(i => isItemComplete(i)).length || 0;
  // Phase 9 — a $0 order (100% discount/coupon, free shipping) never needs a card
  const isFreeOrder  = order.total <= 0;
  const needsPayment = isDirectWholesale && !['captured', 'waived'].includes(order.paymentStatus) && !isFreeOrder;
  const alreadyHandled = isDirectWholesale && ['captured', 'waived'].includes(order.paymentStatus);

  return (
    <div className="page">
      <button className="btn btn-sm" style={{ marginBottom:16 }} onClick={() => navigate('/pending')}>← Pending sales</button>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:4 }}>
        <div className="page-title">{order.customerFirstName} {order.customerLastName}</div>
        <Badge status={order.status} />
      </div>
      <div style={{ fontSize:13, color:'var(--text2)', marginBottom:20 }}>
        {order.orderNumber} · {fmtDate(order.createdAt)} · {fmtMoney(order.total)} · {order.items?.length} items
        {order.roomNumber && <span style={{ marginLeft:8, fontWeight:500 }}>· Room: {order.roomNumber}</span>}
        <span style={{ marginLeft:8, background:'var(--brand-light)', color:'var(--brand)', padding:'1px 8px', borderRadius:20, fontWeight:500, fontSize:11 }}>{order.groupName}</span>
      </div>

      <div className="sidebar">
        <div>
          <div className="card">
            <div style={{ fontWeight:600, marginBottom:4 }}>Upload photos</div>
            <div style={{ fontSize:12, color:'var(--text2)', marginBottom:16 }}>
              Upload front photo for each item. If a back photo reference is set, upload both before sending.
            </div>
            {order.items?.map((item, idx) => {
              const p = photos[item.id] || {};
              const hasBack = !!item.referenceNumber2;
              const backDisabled = p.backDisabled || false;
              const itemDone = isItemComplete(item);
              return (
                <div key={item.id} style={{
                  border:`1px solid ${itemDone?'var(--success-bd)':'var(--border)'}`,
                  borderRadius:'var(--radius)', padding:14, marginBottom:12,
                  background: itemDone?'var(--success-bg)':'var(--surface)',
                }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:10 }}>
                    <div style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
                      {item.productImageUrl && (
                        <img src={item.productImageUrl} alt={item.productName}
                          style={{ width:48, height:48, objectFit:'contain', borderRadius:4, background:'var(--bg)', flexShrink:0, border:'1px solid var(--border)' }} />
                      )}
                      <div>
                        <div style={{ fontWeight:500 }}>Line {idx+1}: {item.productName} × {item.quantity}</div>
                        <div style={{ fontSize:11, color:'var(--text3)' }}>
                          Front: {item.referenceNumber||'—'}
                          {hasBack && <span style={{ marginLeft:8 }}>Back: {item.referenceNumber2}</span>}
                          <span style={{ marginLeft:8 }}>Room: {order.roomNumber||'—'}</span>
                        </div>
                      </div>
                    </div>
                    <Badge status={itemDone?'uploaded':'pending'} />
                  </div>

                  {/* Front photo */}
                  <div style={{ marginBottom: hasBack ? 10 : 0 }}>
                    <div style={{ fontSize:12, fontWeight:600, marginBottom:6, color:'var(--text2)' }}>
                      📷 {item.referenceNumber || 'Front photo'}
                    </div>
                    {p.front ? (
                      <div>
                        <img src={p.front.preview} alt={p.front.name}
                          style={{ width:'100%', maxHeight:160, objectFit:'contain', borderRadius:'var(--radius)', border:'1px solid var(--success-bd)', marginBottom:6, background:'var(--bg)' }}
                          onError={e => { e.target.style.display='none'; }} />
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <span style={{ fontSize:11, color:'var(--text2)', flex:1 }}>✅ {p.front.name}</span>
                          <label className="btn btn-sm" style={{ cursor:'pointer' }}>
                            Replace
                            <input type="file" accept="image/*" style={{ display:'none' }}
                              onChange={e => { setPhotos(prev => ({ ...prev, [item.id]: { ...prev[item.id], front: null } })); e.target.files[0] && handleFileSelect(item, e.target.files[0], false); }} />
                          </label>
                        </div>
                      </div>
                    ) : (
                      <label className="photoslot" style={{ display:'block', cursor:'pointer' }}>
                        <div style={{ fontSize:24, marginBottom:6 }}>📷</div>
                        <div style={{ fontSize:13, fontWeight:500, color:'var(--text2)' }}>Click to upload front photo</div>
                        <div style={{ fontSize:11, color:'var(--text3)' }}>JPG, PNG, TIFF — max 100MB</div>
                        <input type="file" accept="image/*,.tiff,.tif" style={{ display:'none' }}
                          onChange={e => e.target.files[0] && handleFileSelect(item, e.target.files[0], false)} />
                      </label>
                    )}
                  </div>

                  {/* Back photo — only shown if referenceNumber2 set */}
                  {hasBack && (
                    <div style={{ borderTop:'1px solid var(--border)', paddingTop:10 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                        <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)' }}>
                          📷 {item.referenceNumber2} <span style={{ fontWeight:400, color:'var(--text3)' }}>(back)</span>
                        </div>
                        <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, cursor:'pointer', color:'var(--text2)' }}>
                          <input type="checkbox" checked={backDisabled}
                            onChange={() => toggleBackDisabled(item)} />
                          Skip back photo
                        </label>
                      </div>
                      {!backDisabled && (
                        p.back ? (
                          <div>
                            <img src={p.back.preview} alt={p.back.name}
                              style={{ width:'100%', maxHeight:160, objectFit:'contain', borderRadius:'var(--radius)', border:'1px solid var(--success-bd)', marginBottom:6, background:'var(--bg)' }}
                              onError={e => { e.target.style.display='none'; }} />
                            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                              <span style={{ fontSize:11, color:'var(--text2)', flex:1 }}>✅ {p.back.name}</span>
                              <label className="btn btn-sm" style={{ cursor:'pointer' }}>
                                Replace
                                <input type="file" accept="image/*" style={{ display:'none' }}
                                  onChange={e => { setPhotos(prev => ({ ...prev, [item.id]: { ...prev[item.id], back: null } })); e.target.files[0] && handleFileSelect(item, e.target.files[0], true); }} />
                              </label>
                            </div>
                          </div>
                        ) : (
                          <label className="photoslot" style={{ display:'block', cursor:'pointer' }}>
                            <div style={{ fontSize:24, marginBottom:6 }}>📷</div>
                            <div style={{ fontSize:13, fontWeight:500, color:'var(--text2)' }}>Click to upload back photo</div>
                            <div style={{ fontSize:11, color:'var(--text3)' }}>JPG, PNG, TIFF — max 100MB</div>
                            <input type="file" accept="image/*,.tiff,.tif" style={{ display:'none' }}
                              onChange={e => e.target.files[0] && handleFileSelect(item, e.target.files[0], true)} />
                          </label>
                        )
                      )}
                      {backDisabled && <div style={{ fontSize:11, color:'var(--text3)', fontStyle:'italic' }}>Back photo skipped for this item</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <div className="card" style={{ marginBottom:14 }}>
            <div style={{ fontWeight:600, marginBottom:10 }}>Photo status</div>
            {order.items?.map((item, idx) => (
              <div key={item.id} style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                <span style={{ fontSize:12, color:'var(--text2)', flex:1 }}>Line {idx+1}: {item.productName}</span>
                <Badge status={isItemComplete(item)?'uploaded':'pending'} />
              </div>
            ))}
            <hr className="divider" />
            <div style={{ fontSize:12, color:'var(--text2)' }}>{doneCount} of {order.items?.length} items ready</div>
          </div>

          <div className="card" style={{ marginBottom:14 }}>
            <div style={{ fontWeight:600, marginBottom:8 }}>Order total</div>
            <div className="sum-row"><span>Subtotal</span><span>{fmtMoney(order.subtotal)}</span></div>
            {(() => {
              const volumeDiscountTotal = (order.items || []).reduce((s, i) => s + parseFloat(i.volumeDiscountAmount || 0), 0);
              const couponPercentDiscount = order.couponType === 'percent_discount' ? parseFloat(order.couponDiscount || 0) : 0;
              return (
                <>
                  {volumeDiscountTotal > 0 && (
                    <div className="sum-row" style={{ color:'var(--success)' }}>
                      <span>Volume Discount</span><span>-{fmtMoney(volumeDiscountTotal)}</span>
                    </div>
                  )}
                  {couponPercentDiscount > 0 && (
                    <div className="sum-row" style={{ color:'var(--success)' }}>
                      <span>Coupon Discount {order.couponCode ? `(${order.couponCode})` : ''}</span>
                      <span>-{fmtMoney(couponPercentDiscount)}</span>
                    </div>
                  )}
                </>
              );
            })()}
            <div className="sum-row">
              <span>Shipping{order.couponType === 'free_shipping' ? ` (waived — ${order.couponCode})` : ''}</span>
              <span>{fmtMoney(order.shippingCost)}</span>
            </div>
            {order.taxEnabled && <div className="sum-row"><span>Tax</span><span>{fmtMoney(order.taxAmount)}</span></div>}
            <hr className="divider" />
            <div className="sum-total"><span>Total</span><span>{fmtMoney(order.total)}</span></div>
          </div>

          {needsPayment && allDone && (
            <div className="card" style={{ marginBottom:14 }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
                <div style={{ fontWeight:600 }}>Payment</div>
                <div style={{ display:'flex', gap:5 }}>
                  <svg width="30" height="20" viewBox="0 0 30 20"><rect width="30" height="20" rx="3" fill="#1A1F71"/><text x="15" y="13.5" fontSize="7.5" fontWeight="700" fontStyle="italic" fill="#fff" textAnchor="middle" fontFamily="Arial, sans-serif">VISA</text></svg>
                  <svg width="30" height="20" viewBox="0 0 30 20"><rect width="30" height="20" rx="3" fill="#16172A"/><circle cx="12" cy="10" r="6" fill="#EB001B"/><circle cx="18" cy="10" r="6" fill="#F79E1B" opacity="0.9"/></svg>
                  <svg width="30" height="20" viewBox="0 0 30 20"><rect width="30" height="20" rx="3" fill="#006FCF"/><text x="15" y="13" fontSize="6.5" fontWeight="700" fill="#fff" textAnchor="middle" fontFamily="Arial, sans-serif">AMEX</text></svg>
                  <svg width="30" height="20" viewBox="0 0 30 20"><rect width="30" height="20" rx="3" fill="#f5f5f5" stroke="var(--border)"/><circle cx="9" cy="10" r="5" fill="#FF6000"/><text x="20" y="13" fontSize="5.5" fontWeight="700" fill="#111" textAnchor="middle" fontFamily="Arial, sans-serif">DISC</text></svg>
                </div>
              </div>
              <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, marginBottom:10, cursor:'pointer' }}>
                <input type="checkbox" checked={billingSameAsShipping}
                  onChange={e => setBillingSameAsShipping(e.target.checked)} />
                Billing address same as shipping
              </label>
              {!billingSameAsShipping && (
                <div style={{ marginBottom:10 }}>
                  <div className="field"><label className="label">Address line 1</label><input ref={billingAddress1Ref} className="input" value={billingAddress.address1} onChange={e => setBillingAddress(p => ({ ...p, address1:e.target.value }))} placeholder="123 Main St or APT 302 - 123 Main St" /></div>
                  <div className="field"><label className="label">Address line 2</label><input className="input" value={billingAddress.address2} onChange={e => setBillingAddress(p => ({ ...p, address2:e.target.value }))} placeholder="Apt, suite, unit (optional)" /></div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                    <div className="field"><label className="label">City</label><input className="input" value={billingAddress.city} onChange={e => setBillingAddress(p => ({ ...p, city:e.target.value }))} /></div>
                    <div className="field">
                      <label className="label">{billingAddress.country === 'CA' ? 'Province' : 'State'}</label>
                      <select className="input" value={billingAddress.province} onChange={e => setBillingAddress(p => ({ ...p, province:e.target.value }))}>
                        <option value="">—</option>
                        {(billingAddress.country === 'CA' ? CA_PROVINCES : US_STATES).map(s => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                    <div className="field"><label className="label">ZIP / Postal</label><input className="input" value={billingAddress.zip} onChange={e => setBillingAddress(p => ({ ...p, zip:e.target.value }))} /></div>
                    <div className="field"><label className="label">Country</label><CountrySelect value={billingAddress.country || 'US'} onChange={code => setBillingAddress(p => ({ ...p, country:code, province:'' }))} /></div>
                  </div>
                </div>
              )}
              <div style={{
                border:'1px solid var(--border)', borderRadius:'var(--radius)',
                padding:'12px 14px', background:'var(--surface)',
              }}>
                <CardElement options={{ style: { base: { fontSize: '15px', color: 'var(--text)' } } }} />
              </div>
              {paymentError && (
                <div style={{ color:'var(--danger)', fontSize:12, marginTop:8 }}>{paymentError}</div>
              )}
            </div>
          )}

          {isDirectWholesale && isFreeOrder && !alreadyHandled && (
            <div className="alert alert-success" style={{ marginBottom:14 }}>🎉 This order totals $0 — no payment required.</div>
          )}

          {alreadyHandled && (
            <div className="alert alert-success" style={{ marginBottom:14 }}>
              {order.paymentStatus === 'waived' ? '🎉 No payment required for this $0 order.' : '✅ Payment already received for this order.'}
            </div>
          )}

          <button className={`btn btn-full btn-lg ${allDone?'btn-primary':''}`}
            disabled={!allDone || fulfilling || (needsPayment && (!stripe || !elements || !billingReady))}
            onClick={handleSendToProduction}>
            {fulfilling
              ? (needsPayment ? 'Processing payment…' : 'Uploading photos…')
              : (needsPayment ? 'Charge Card & Send to Modeling →' : 'Send to Modeling →')}
          </button>
          {!allDone && <div style={{ fontSize:11, color:'var(--text3)', marginTop:8, textAlign:'center' }}>Upload all required photos to continue</div>}
        </div>
      </div>
    </div>
  );
}

// Public export — wraps the screen in Stripe's Elements provider so useStripe()/
// useElements() (and <CardElement>) work inside UploadScreenInner. Harmless for
// invoice_retail companies too — stripe is just null/unused if no key is set.
export function UploadScreen() {
  const { company } = useAuth();
  const isDirectWholesale = company?.billingModel === 'direct_wholesale';
  return (
    <Elements stripe={isDirectWholesale ? getStripePromise() : null}>
      <UploadScreenInner />
    </Elements>
  );
}

// ═══════════════════════════════════════════════════════════════
//  RESOURCES SCREEN
// ═══════════════════════════════════════════════════════════════
export function ResourcesScreen() {
  const [videos, setVideos] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loadingVideos, setLoadingVideos] = useState(true);

  useEffect(() => {
    getTrainingVideos()
      .then(r => {
        const list = r.data?.videos || [];
        setVideos(list);
        if (list.length) setActiveId(list[0].id);
      })
      .catch(() => setVideos([]))
      .finally(() => setLoadingVideos(false));
  }, []);

  const activeVideo = videos.find(v => v.id === activeId);

  return (
    <div className="page">
      <div style={{ marginBottom:24 }}>
        <div className="page-title">Resources</div>
        <div className="page-sub">Training materials and photo shoot guidelines</div>
      </div>

      {/* Training videos (Phase 9) */}
      <div className="card" style={{ marginBottom:24 }}>
        <div style={{ fontWeight:600, marginBottom:14 }}>Training Videos</div>
        {loadingVideos ? (
          <Loading label="Loading videos…" />
        ) : videos.length === 0 ? (
          <div style={{ color:'var(--text3)', fontSize:13 }}>No training videos available yet.</div>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 260px', gap:16 }}>
            <div>
              {activeVideo && (
                <video
                  key={activeVideo.id}
                  src={trainingVideoUrl(activeVideo.id)}
                  controls
                  style={{ width:'100%', maxHeight:420, borderRadius:'var(--radius)', background:'#000', display:'block' }}
                />
              )}
              {activeVideo && (
                <div style={{ fontSize:14, fontWeight:500, marginTop:10 }}>{activeVideo.title}</div>
              )}
            </div>
            <div>
              {videos.map((v, i) => (
                <button key={v.id} onClick={() => setActiveId(v.id)}
                  className="btn"
                  style={{
                    width:'100%', textAlign:'left', marginBottom:8, display:'flex', alignItems:'center', gap:10,
                    background: v.id === activeId ? 'var(--brand)' : '',
                    color:      v.id === activeId ? '#fff' : '',
                    borderColor: v.id === activeId ? 'var(--brand)' : '',
                  }}>
                  <span style={{ fontSize:11, fontWeight:700, opacity:0.7, flexShrink:0 }}>{i + 1}</span>
                  <span style={{ fontSize:13 }}>{v.title}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Photo shoot specifications (existing) */}
      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        <iframe
          src="/resources/photo-specifications.html"
          title="Photo Shoot Specifications"
          style={{ width:'100%', height:'calc(100vh - 160px)', border:'none', display:'block' }}
        />
      </div>
    </div>
  );
}
